import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'teacher' | 'student'
  avatar?: string
  subscription: 'free' | 'premium' | 'enterprise'
  lastLogin: string
  twoFactorEnabled: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<void>
  googleLogin: (token: string) => Promise<void>
  logout: () => void
  updateUser: (data: Partial<User>) => void
  refreshToken: () => Promise<void>
}

const API_BASE = import.meta.env.VITE_API_URL || '/api'

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: true,

      login: async (email: string, password: string) => {
        const res = await fetch(`${API_BASE}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error)

        set({ user: data.user, token: data.token, isLoading: false })
        localStorage.setItem('mazakertesh_token', data.token)
      },

      register: async (name: string, email: string, password: string) => {
        const res = await fetch(`${API_BASE}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, password })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error)

        set({ user: data.user, token: data.token, isLoading: false })
        localStorage.setItem('mazakertesh_token', data.token)
      },

      googleLogin: async (googleToken: string) => {
        const res = await fetch(`${API_BASE}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: googleToken })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error)

        set({ user: data.user, token: data.token, isLoading: false })
        localStorage.setItem('mazakertesh_token', data.token)
      },

      logout: () => {
        localStorage.removeItem('mazakertesh_token')
        set({ user: null, token: null, isLoading: false })
        window.location.href = '/'
      },

      updateUser: (data) => {
        const current = get().user
        if (current) set({ user: { ...current, ...data } })
      },

      refreshToken: async () => {
        const token = localStorage.getItem('mazakertesh_token')
        if (!token) {
          set({ isLoading: false })
          return
        }
        try {
          const res = await fetch(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (res.ok) {
            const data = await res.json()
            set({ user: data.user, token, isLoading: false })
          } else {
            localStorage.removeItem('mazakertesh_token')
            set({ user: null, token: null, isLoading: false })
          }
        } catch {
          set({ isLoading: false })
        }
      }
    }),
    { name: 'mazakertesh-auth' }
  )
)

// Auto-refresh on load
setTimeout(() => useAuthStore.getState().refreshToken(), 100)
